import { Card, Empty, Skeleton } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import {
  firstFieldError,
  getResponseMessage
} from "../../../utils/handlers/get-response-message"
import { customersSetProp } from "../redux/customers.slice.ts"
import { updateCustomer } from "../redux/customers.thunks.ts"
import { type CustomerMutateData } from "../types/customer.types.ts"
import { CustomerDetail } from "./CustomerDetail.tsx"
import { clearCustomerDetail } from "./redux/customer-detail.slice.ts"
import {
  fetchCustomerDetail,
  fetchCustomerLeads,
  fetchCustomerProjects
} from "./redux/customer-detail.thunks.ts"

const CustomerDetailPage = ({
  customer,
  fetching,
  notFound,
  leads,
  projects,
  canEdit,
  getCustomer,
  getCustomerLeads,
  getCustomerProjects,
  updateCustomerById,
  setCustomersProp,
  clearDetail
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const id = useParams()?.id as string

  useEffect(() => {
    generateDocumentTitle("Customer")

    return () => {
      clearDetail()
    }
  }, [])

  useEffect(() => {
    if (!id) return

    getCustomer(id)
    getCustomerLeads({ customer: id })
    getCustomerProjects({ customer: id })
  }, [id])

  if (fetching && !customer)
    return <Skeleton.Node active className={"size-full"} />

  if (!customer || notFound) {
    return (
      <Card size={"small"}>
        <Empty description={t("customerDetail.notFound")} />
      </Card>
    )
  }

  const handleSave = async (changed: CustomerMutateData) => {
    try {
      await updateCustomerById({ id, ...changed }).unwrap()
      setCustomersProp({ key: "saveResult", value: null })
      return true
    } catch (rejection) {
      const message =
        getResponseMessage(rejection) || firstFieldError(rejection)

      if (message) throw new Error(message)
      return false
    }
  }

  return (
    <CustomerDetail
      customer={customer}
      canEdit={canEdit}
      leads={leads}
      projects={projects}
      onSave={handleSave}
      onLeadsLoadMore={() => {
        const url = nextPageUrl(leads)
        if (!url) return
        getCustomerLeads({ customer: id, ...nextPageOf(url) })
      }}
      onProjectsLoadMore={() => {
        const url = nextPageUrl(projects)
        if (!url) return
        getCustomerProjects({ customer: id, ...nextPageOf(url) })
      }}
    />
  )
}

/** The page number the server's own `next` names, plus the flag that tells the
 *  reducer to append. */
const nextPageOf = (next: string) => ({
  page: Number(extractParamsFromNextUrl<{ page?: string }>(next).page ?? 2),
  isMore: true
})

const mapStateToProps = (state: RootState) => ({
  customer: state.customerDetail.customer,
  fetching: state.customerDetail.fetching,
  notFound: state.customerDetail.notFound,
  leads: state.customerDetail.leads,
  projects: state.customerDetail.projects,
  canEdit: !!state.auth?.permissions?.codes?.customers?.customers?.customer_edit
})

const mapDispatchToProps = {
  getCustomer: fetchCustomerDetail,
  getCustomerLeads: fetchCustomerLeads,
  getCustomerProjects: fetchCustomerProjects,
  updateCustomerById: updateCustomer,
  setCustomersProp: customersSetProp,
  clearDetail: clearCustomerDetail
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(CustomerDetailPage)
